import React from "react";
import { Download, Monitor, Cog, Zap, ShieldAlert } from "lucide-react";
import { useDesktopInfo, useAdapters } from "@/lib/queries";

// Direct installer download from GitHub Releases — built by .github/workflows/build-windows.yml
const GITHUB_REPO_URL = "https://github.com/juansolo9000/cardradar";
const EXE_DOWNLOAD_URL = `${GITHUB_REPO_URL}/releases/latest/download/CardRadar-Setup.exe`;



export default function DesktopPage() {
  const { data } = useDesktopInfo();
  const { data: adapters = [] } = useAdapters();
  const appUrl = window.location.origin;

  return (
    <div className="p-6 max-w-4xl w-full mx-auto" data-testid="desktop-page">
      <header className="mb-6">
        <h1 className="font-heading text-4xl tracking-tighter font-black uppercase">
          Download <span className="text-[#EAB308]">CardRadar Installer</span>
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Download CardRadar-Setup.exe, run the installer, you&apos;re done.
        </p>
      </header>

      {/* Why local? */}
      <div className="bg-[#EAB308]/5 border border-[#EAB308]/30 px-4 py-3 rounded-sm mb-6 flex gap-3" data-testid="why-local">
        <Zap className="w-5 h-5 text-[#EAB308] shrink-0 mt-0.5" />
        <div className="text-[12px] leading-relaxed text-[#D4D4D4]">
          <strong className="text-white">Why install locally?</strong> Australian big-box retailers (Target, Kmart, BIG W, EB Games) and most US/AU stores block cloud datacenter IPs with Akamai/DataDome — that&apos;s why the cloud feed shows mostly TCGplayer LIVE and the rest as DEMO. The <code className="text-[#EAB308]">.exe</code> runs from your home IP, which isn&apos;t on those blocklists, so it pulls <strong className="text-white">real listings from every retailer</strong> + unlocks 1-Click Cart and auto-checkout. Free with your subscription.
        </div>
      </div>

      {/* Windows SmartScreen warning — every unsigned .exe trips this */}
      <div className="bg-[#1E3A8A]/15 border border-[#3B82F6]/30 px-4 py-3 rounded-sm mb-6 flex gap-3" data-testid="smartscreen-warning">
        <ShieldAlert className="w-5 h-5 text-[#93C5FD] shrink-0 mt-0.5" />
        <div className="text-[12px] leading-relaxed text-[#D4D4D4]">
          <strong className="text-white">Windows will warn you the first time you run the .exe</strong> — a blue popup that says <em>&ldquo;Windows protected your PC&rdquo;</em>. This is normal for every unsigned app on the planet. Click <strong className="text-white">More info</strong>, then <strong className="text-white">Run anyway</strong>. The download is hosted directly from CardRadar&apos;s public GitHub release; you can inspect the source any time.
        </div>
      </div>

      {/* Cloud vs Desktop comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-testid="cloud-vs-desktop">
        <div className="bg-[#0d0d0d] border border-[#262626] p-4 rounded-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 border border-[#525252] flex items-center justify-center rounded-sm">
              <Monitor className="w-3.5 h-3.5 text-[#737373]" />
            </div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#A3A3A3]">Cloud only</h3>
            <span className="text-[10px] font-mono uppercase text-[#525252] ml-auto">included</span>
          </div>
          <ul className="space-y-1.5 text-xs text-[#A3A3A3]">
            <li>✓ TCGplayer live listings</li>
            <li className="text-[#525252]">— DEMO placeholders for other retailers</li>
            <li className="text-[#525252]">— Open Cart goes to category page (not exact product)</li>
            <li className="text-[#525252]">— No 1-Click Cart</li>
            <li className="text-[#525252]">— No auto-checkout (mocked only)</li>
            <li className="text-[#525252]">— Watchlist alerts still work</li>
          </ul>
        </div>
        <div className="bg-[#EAB308]/5 border-2 border-[#EAB308] p-4 rounded-sm relative">
          <div className="absolute -top-2.5 right-3 bg-[#EAB308] text-black text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm">recommended</div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 border border-[#EAB308] flex items-center justify-center rounded-sm bg-[#EAB308]/10">
              <Zap className="w-3.5 h-3.5 text-[#EAB308]" />
            </div>
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider">Desktop .exe</h3>
            <span className="text-[10px] font-mono uppercase text-[#EAB308] ml-auto">free with sub</span>
          </div>
          <ul className="space-y-1.5 text-xs text-white">
            <li>✓ <strong>15 retailers LIVE</strong> (JB Hi-Fi, Target AU, Kmart, BIG W, EB Games, Pokemon Center, +9 more)</li>
            <li>✓ Open Cart lands on the exact product page</li>
            <li>✓ <strong>1-Click Cart</strong> — auto-adds item to retailer cart</li>
            <li>✓ <strong>Auto-checkout</strong> — Playwright fills card + address, you press Place Order</li>
            <li>✓ Encrypted local vault for store accounts, cards, addresses</li>
            <li>✓ Watchlist alerts also work</li>
          </ul>
        </div>
      </div>

      {/* 2 simple steps */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div className="bg-[#0d0d0d] border border-[#262626] p-4 rounded-sm">
          <Download className="w-5 h-5 text-[#EAB308] mb-2" />
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">Step 1</div>
          <div className="text-sm text-white font-semibold leading-tight mt-1">Download CardRadar-Setup.exe</div>
          <div className="text-xs text-[#A3A3A3] mt-1">
            ~60 MB installer. Hosted directly from CardRadar&apos;s GitHub release. Installs to Program Files in seconds.
          </div>
        </div>
        <div className="bg-[#0d0d0d] border border-[#262626] p-4 rounded-sm">
          <Zap className="w-5 h-5 text-[#EAB308] mb-2" />
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373]">Step 2</div>
          <div className="text-sm text-white font-semibold leading-tight mt-1">Run the installer, then launch</div>
          <div className="text-xs text-[#A3A3A3] mt-1">
            The installer adds a desktop + Start Menu shortcut. First launch downloads Chromium (~150 MB, one-time, cached). Then log in with your CardRadar account.
          </div>
        </div>
      </div>

      {/* Big download button — points at GitHub Releases */}
      <a
        data-testid="download-exe"
        href={EXE_DOWNLOAD_URL}
        rel="noopener noreferrer"
        className="block w-full bg-[#EAB308] text-black font-bold text-base uppercase tracking-wider px-5 py-4 rounded-sm hover:bg-[#FDE047] transition-all text-center flex items-center justify-center gap-3 mb-2 hover:shadow-[0_0_22px_rgba(234,179,8,0.45)]"
      >
        <Download className="w-5 h-5" />
        Download CardRadar-Setup.exe (Windows 10/11, ~60 MB)
      </a>
      <div className="text-[11px] text-[#737373] text-center mb-6">
        Hosted on <a href={GITHUB_REPO_URL} target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">GitHub</a> — auto-built by GitHub Actions on every commit. <a href={`${GITHUB_REPO_URL}/releases/latest`} target="_blank" rel="noopener noreferrer" className="text-[#EAB308] hover:underline">View all releases →</a>
      </div>

      <div className="bg-[#0d0d0d] border border-[#262626] p-4 rounded-sm mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#737373] mb-1">App URL embedded</div>
        <div className="font-mono text-sm text-white break-all" data-testid="app-url">
          {data?.app_url || appUrl}
        </div>
        <div className="text-[11px] text-[#737373] mt-2">
          The local <code className="text-[#EAB308]">.exe</code> phones home to this URL to validate your subscription on launch. Your accounts, cards and proxies all stay on your machine in an encrypted SQLite file — never uploaded.
        </div>
      </div>

      {/* Adapter status */}
      <div className="bg-[#0d0d0d] border border-[#262626] rounded-sm overflow-hidden" data-testid="adapter-status">
        <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2">
          <Cog className="w-4 h-4 text-[#A855F7]" />
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider">Retailer auto-purchase adapters</h3>
          <span className="text-[10px] font-mono text-[#737373] ml-auto">
            {adapters.filter((a) => a.has_adapter).length}/{adapters.length} ready
          </span>
        </div>
        <div className="p-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
          {adapters.map((a) => (
            <div key={a.retailer_id} className="flex items-center justify-between px-2 py-1.5 bg-[#0a0a0a] border border-[#262626] rounded-sm">
              <span className="text-white truncate">{a.retailer_name}</span>
              {a.has_adapter ? (
                <span className="text-[10px] font-mono text-[#22C55E] uppercase">ready</span>
              ) : (
                <span className="text-[10px] font-mono text-[#525252] uppercase">soon</span>
              )}
            </div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-[#262626] text-[11px] text-[#737373]">
          The .exe ships with Playwright adapters for Pokemon Center, TCGplayer and JB Hi-Fi. Each one logs in with your saved retailer account, drops the item in the cart, autofills the saved card + address, and stops at the Place Order button so you keep the final click.
        </div>
      </div>
    </div>
  );
}
